import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import {
  FileText, Wand2, Upload, Copy, Download, Loader2,
  RefreshCw, Eye, Check, AlertCircle, Sparkles, ScanText
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DocumentosAuto() {
  const { toast } = useToast();
  const [tabActiva, setTabActiva] = useState('generar');
  const [loading, setLoading] = useState(false);
  const [placeholders, setPlaceholders] = useState([]);
  const [plantillas, setPlantillas] = useState([]);
  const [expedientes, setExpedientes] = useState([]);

  // Estado para generación
  const [plantillaId, setPlantillaId] = useState('');
  const [expedienteId, setExpedienteId] = useState('');
  const [datosAdicionales, setDatosAdicionales] = useState({});
  const [documentoGenerado, setDocumentoGenerado] = useState(null);

  // Estado para OCR
  const [archivoOCR, setArchivoOCR] = useState(null);
  const [resultadoOCR, setResultadoOCR] = useState(null);
  const [idiomaOCR, setIdiomaOCR] = useState('spa');

  useEffect(() => {
    cargarDatosIniciales();
  }, []);

  const cargarDatosIniciales = async () => {
    try {
      // Cargar placeholders disponibles
      const phResponse = await axios.get(`${API}/documentos-auto/placeholders`);
      setPlaceholders(phResponse.data.placeholders || []);

      // Cargar plantillas
      const plantResponse = await axios.get(`${API}/plantillas/`);
      setPlantillas(plantResponse.data.plantillas || []);

      // Cargar expedientes
      const expResponse = await axios.get(`${API}/expedientes?limit=100`);
      setExpedientes(expResponse.data.expedientes || []);
    } catch (err) {
      console.error('Error cargando datos:', err);
    }
  };

  const generarDocumento = async () => {
    if (!plantillaId || !expedienteId) {
      toast({ title: 'Error', description: 'Seleccione plantilla y expediente', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/documentos-auto/generar`, {
        plantilla_id: parseInt(plantillaId),
        expediente_id: parseInt(expedienteId),
        datos_adicionales: datosAdicionales
      });

      setDocumentoGenerado(response.data);
      toast({ title: 'Documento generado', description: 'El documento se ha generado correctamente' });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Error al generar', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const copiarDocumento = () => {
    if (documentoGenerado?.documento) {
      navigator.clipboard.writeText(documentoGenerado.documento);
      toast({ title: 'Copiado', description: 'Documento copiado al portapapeles' });
    }
  };

  const procesarOCR = async () => {
    if (!archivoOCR) {
      toast({ title: 'Error', description: 'Seleccione un archivo', variant: 'destructive' });
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('archivo', archivoOCR);

    try {
      const response = await axios.post(`${API}/documentos-auto/ocr?idioma=${idiomaOCR}`, formData);
      setResultadoOCR(response.data);
      toast({ title: 'OCR completado', description: 'Texto extraído correctamente' });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Error en OCR', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Wand2 className="h-6 w-6 text-purple-600" />
          Automatización de Documentos
        </h1>
        <p className="text-gray-600">Generar documentos desde plantillas y extraer texto con OCR</p>
      </div>

      <Tabs value={tabActiva} onValueChange={setTabActiva}>
        <TabsList className="mb-4">
          <TabsTrigger value="generar" className="flex items-center gap-2">
            <FileText className="h-4 w-4" /> Generar Documento
          </TabsTrigger>
          <TabsTrigger value="placeholders" className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" /> Placeholders
          </TabsTrigger>
          <TabsTrigger value="ocr" className="flex items-center gap-2">
            <ScanText className="h-4 w-4" /> OCR
          </TabsTrigger>
        </TabsList>

        {/* Generar Documento */}
        <TabsContent value="generar">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Configuración */}
            <Card>
              <CardHeader>
                <CardTitle>Configuración</CardTitle>
                <CardDescription>Seleccione plantilla y expediente para generar el documento</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Plantilla *</Label>
                  <Select value={plantillaId} onValueChange={setPlantillaId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar plantilla" />
                    </SelectTrigger>
                    <SelectContent>
                      {plantillas.map((p) => (
                        <SelectItem key={p.id} value={p.id.toString()}>
                          {p.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Expediente *</Label>
                  <Select value={expedienteId} onValueChange={setExpedienteId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar expediente" />
                    </SelectTrigger>
                    <SelectContent>
                      {expedientes.map((e) => (
                        <SelectItem key={e.id} value={e.id.toString()}>
                          {e.numero_expediente} - {e.titulo}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Datos adicionales (opcional)</Label>
                  <p className="text-xs text-gray-500 mb-2">
                    Añada datos personalizados que no estén en el expediente
                  </p>
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        placeholder="Nombre del campo"
                        id="campo-nombre"
                      />
                      <Input
                        placeholder="Valor"
                        id="campo-valor"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const nombre = document.getElementById('campo-nombre').value;
                          const valor = document.getElementById('campo-valor').value;
                          if (nombre && valor) {
                            setDatosAdicionales({ ...datosAdicionales, [nombre]: valor });
                            document.getElementById('campo-nombre').value = '';
                            document.getElementById('campo-valor').value = '';
                          }
                        }}
                      >
                        Añadir
                      </Button>
                    </div>
                    {Object.entries(datosAdicionales).length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {Object.entries(datosAdicionales).map(([k, v]) => (
                          <Badge key={k} variant="secondary" className="cursor-pointer" onClick={() => {
                            const newDatos = { ...datosAdicionales };
                            delete newDatos[k];
                            setDatosAdicionales(newDatos);
                          }}>
                            {k}: {v} ×
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <Button onClick={generarDocumento} disabled={loading || !plantillaId || !expedienteId} className="w-full">
                  {loading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generando...</>
                  ) : (
                    <><Wand2 className="h-4 w-4 mr-2" /> Generar Documento</>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Resultado */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Documento Generado</CardTitle>
                  {documentoGenerado && (
                    <CardDescription>
                      {documentoGenerado.nombre_sugerido}
                    </CardDescription>
                  )}
                </div>
                {documentoGenerado && (
                  <Button variant="outline" size="sm" onClick={copiarDocumento}>
                    <Copy className="h-4 w-4 mr-1" /> Copiar
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {documentoGenerado ? (
                  <>
                    {/* Info de placeholders */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {documentoGenerado.placeholders_completados?.map((ph) => (
                        <Badge key={ph} className="bg-green-100 text-green-700">
                          <Check className="h-3 w-3 mr-1" /> {ph}
                        </Badge>
                      ))}
                      {documentoGenerado.placeholders_vacios?.map((ph) => (
                        <Badge key={ph} className="bg-yellow-100 text-yellow-700">
                          <AlertCircle className="h-3 w-3 mr-1" /> {ph}
                        </Badge>
                      ))}
                    </div>

                    {/* Documento */}
                    <div className="border rounded-lg p-4 bg-gray-50 max-h-96 overflow-y-auto">
                      <pre className="whitespace-pre-wrap text-sm font-mono">
                        {documentoGenerado.documento}
                      </pre>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>El documento generado aparecerá aquí</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Placeholders */}
        <TabsContent value="placeholders">
          <Card>
            <CardHeader>
              <CardTitle>Placeholders Disponibles</CardTitle>
              <CardDescription>
                Use estos códigos en sus plantillas para auto-completar datos del expediente
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {placeholders.map((ph) => (
                  <div key={ph.codigo} className="bg-gray-50 p-3 rounded-lg">
                    <code className="text-sm font-mono text-purple-600 block mb-1">
                      {ph.codigo}
                    </code>
                    <p className="text-xs text-gray-600">{ph.descripcion}</p>
                  </div>
                ))}
              </div>

              {placeholders.length === 0 && (
                <p className="text-center text-gray-500 py-8">
                  No se encontraron placeholders
                </p>
              )}

              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">Cómo usar</h4>
                <ol className="text-sm text-blue-800 space-y-1">
                  <li>1. Cree una plantilla de documento en la sección de Plantillas</li>
                  <li>2. Incluya los placeholders donde quiera que aparezcan los datos</li>
                  <li>3. Use esta herramienta para generar el documento final</li>
                </ol>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* OCR */}
        <TabsContent value="ocr">
          <div className="grid lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Extracción de Texto (OCR)</CardTitle>
                <CardDescription>Extraiga texto de imágenes escaneadas</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Archivo de imagen</Label>
                  <Input
                    type="file"
                    accept=".png,.jpg,.jpeg,.tiff,.bmp,.pdf"
                    onChange={(e) => setArchivoOCR(e.target.files[0])}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Formatos: PNG, JPG, TIFF, BMP, PDF
                  </p>
                </div>

                <div>
                  <Label>Idioma</Label>
                  <Select value={idiomaOCR} onValueChange={setIdiomaOCR}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="spa">Español</SelectItem>
                      <SelectItem value="cat">Catalán</SelectItem>
                      <SelectItem value="eng">Inglés</SelectItem>
                      <SelectItem value="fra">Francés</SelectItem>
                      <SelectItem value="deu">Alemán</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button onClick={procesarOCR} disabled={loading || !archivoOCR} className="w-full">
                  {loading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Procesando...</>
                  ) : (
                    <><ScanText className="h-4 w-4 mr-2" /> Extraer Texto</>
                  )}
                </Button>

                {!resultadoOCR?.success && resultadoOCR?.error && (
                  <div className="p-3 bg-yellow-50 rounded-lg text-sm text-yellow-800">
                    <AlertCircle className="h-4 w-4 inline mr-2" />
                    {resultadoOCR.error}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Resultado OCR</CardTitle>
                {resultadoOCR?.success && (
                  <CardDescription>
                    {resultadoOCR.caracteres} caracteres, {resultadoOCR.lineas} líneas
                  </CardDescription>
                )}
              </CardHeader>
              <CardContent>
                {resultadoOCR?.success ? (
                  <>
                    {/* Datos detectados */}
                    {resultadoOCR.datos_detectados && (
                      <div className="mb-4 space-y-2">
                        {resultadoOCR.datos_detectados.fechas?.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            <span className="text-xs text-gray-500">Fechas:</span>
                            {resultadoOCR.datos_detectados.fechas.map((f, i) => (
                              <Badge key={i} variant="outline">{f}</Badge>
                            ))}
                          </div>
                        )}
                        {resultadoOCR.datos_detectados.nifs?.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            <span className="text-xs text-gray-500">NIFs:</span>
                            {resultadoOCR.datos_detectados.nifs.map((n, i) => (
                              <Badge key={i} variant="outline">{n}</Badge>
                            ))}
                          </div>
                        )}
                        {resultadoOCR.datos_detectados.importes?.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            <span className="text-xs text-gray-500">Importes:</span>
                            {resultadoOCR.datos_detectados.importes.map((im, i) => (
                              <Badge key={i} variant="outline">{im}</Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Texto */}
                    <div className="border rounded-lg p-4 bg-gray-50 max-h-80 overflow-y-auto">
                      <pre className="whitespace-pre-wrap text-sm">
                        {resultadoOCR.texto}
                      </pre>
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={() => {
                        navigator.clipboard.writeText(resultadoOCR.texto);
                        toast({ title: 'Copiado' });
                      }}
                    >
                      <Copy className="h-4 w-4 mr-1" /> Copiar texto
                    </Button>
                  </>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <ScanText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>El texto extraído aparecerá aquí</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
